import * as mobx from 'mobx'
import { observer } from 'mobx-react'
import PT from 'prop-types'
import * as React from 'react'

import type { CustomNode } from '../../models/Nodes/CustomNode'
import type { SelectNode } from '../../models/Nodes/SelectNode'
import type { ValuedNode, Values } from '../../models/Nodes/ValuedNode'

const STARTING_NODE_ID = '1yjkK68vGY4meI0y2a2UiI'
const QUERY_PREFIX = '?data='

type QueryAttributes = {
  client_id?: string
  user_id?: number
  user_email?: string
  user_firstname?: string
  user_lastname?: string
  cars?: Array<[string, string, string]>
}

export type InputAttributes = {
  client_id: string | null
  user: {
    id: number
    fullName?: string | null
    firstName?: string | null
    lastName?: string | null
    email?: string | null
  }
  cars: Array<{
    brand: string
    model: string
    plate: string
  }>
}

export type Transitioner = {
  getAttributes?: () => InputAttributes | null
  getValueCollection: () => Values
  getCurrentNode: (arg?: boolean) => SelectNode | CustomNode | ValuedNode
  goForward: (arg: string) => SelectNode | CustomNode | ValuedNode
  goBack: () => SelectNode | CustomNode | ValuedNode
  startOver: () => SelectNode | CustomNode | ValuedNode
  openExternal: (arg: string) => void
}

type Props = {
  children: React.ReactNode
  history: object
}

class TransitionProvider extends React.Component<Props, void> {
  constructor(props: any, ctx: any) {
    super(props, ctx)
    // @ts-expect-error@2339
    this.nodes = ctx.models.nodes
    // @ts-expect-error@2339
    this.props.history.listen((location) => {
      this.locationPathname.set(location.pathname)
    })
  }

  getChildContext() {
    return {
      transitioner: this.transitioner,
    }
  }
  // @ts-expect-error@2339
  locationPathname = mobx.observable.box(this.props.history.location.pathname)
  _attributes = null

  _tryReadAttributes() {
    // @ts-expect-error@2339
    const query = this.props.history.location.search
    const data =
      query && query.length && query.split(QUERY_PREFIX).length > 1
        ? query.split(QUERY_PREFIX)[1]
        : null
    if (this._attributes === null && data) {
      const decodedData = decodeURIComponent(data)
      try {
        const jsonObject: QueryAttributes = JSON.parse(decodedData)
        this._setAttributes(jsonObject)
      } catch (_e) {
        this._attributes = undefined
        return
      }
    }
  }

  _setAttributes(input) {
    if (input) {
      this._attributes = {
        client_id: input.client_id,
        user: {
          id: input.user_id,
          fullName:
            input.user_firstname && input.user_lastname
              ? `${input.user_firstname} ${input.user_lastname}`
              : input.user_firstname || input.user_lastname,
          firstName: input.user_firstname,
          lastName: input.user_lastname,
          email: input.user_email,
        },
        cars:
          input.cars && input.cars.length
            ? input.cars.map((car) => ({
                brand: car[0],
                model: car[1],
                plate: car[2],
              }))
            : [],
      }
    }
  }

  _getAttributes() {
    return this._attributes
  }

  get _pathArray() {
    return this.locationPathname
      .get()
      .split('/')
      .filter((id) => id.length > 0)
  }

  _getCurrentNode(valued) {
    this._tryReadAttributes()
    const existingPath =
      this._pathArray.length &&
      // @ts-expect-error@2339
      this.nodes.get(this._pathArray[this._pathArray.length - 1])
    return valued
      ? existingPath
      : (existingPath && this._goForward(existingPath.id, true)) ||
          // @ts-expect-error@2554
          this._goForward(STARTING_NODE_ID)
  }

  _getValueCollection() {
    // @ts-expect-error@2339
    return this.nodes.getValueCollection(this._pathArray)
  }

  _goForward(nextNodeId, addedBreadcrumb) {
    // @ts-expect-error@2339
    const nextNode = this.nodes.get(nextNodeId)
    if (nextNode) {
      const bc =
        this._pathArray[this._pathArray.length - 1] === nextNodeId
          ? // @ts-expect-error@2339
            this.props.history.location.pathname
          : // @ts-expect-error@2339
            `${this.props.history.location.pathname}${nextNodeId}/`
      switch (nextNode.contentModelType) {
        case 'valuedNode':
          // @ts-expect-error@2339
          this.props.history.push(bc)
          return this._goForward(nextNode.baseNode.id, true)
        default:
          // @ts-expect-error@2339
          if (!addedBreadcrumb) this.props.history.push(bc)
          return nextNode
      }
    }
  }

  _goBack() {
    if (this._pathArray.length > 1) {
      const pastNodeId = this._pathArray[this._pathArray.length - 2]
      // @ts-expect-error@2339
      const pastNode = this.nodes.get(pastNodeId)
      if (pastNode) {
        const bc = `/${this._pathArray
          .slice(0, this._pathArray.length - 1)
          .join('/')}/`
        // @ts-expect-error@2339
        this.props.history.push(bc)
        if (pastNode.contentModelType === 'valuedNode') {
          if (pastNode.baseNode.contentModelType === 'valuedNode') {
            return this._goBack()
          }
          return pastNode.baseNode
        }
        return pastNode
      }
    } else {
      return this._startOver()
    }
  }

  _startOver() {
    const initialStepId =
      (this._pathArray.length && this._pathArray[0]) || STARTING_NODE_ID
    const node =
      // @ts-expect-error@2339
      this.nodes.selectNodes.get(initialStepId) ||
      // @ts-expect-error@2339
      this.nodes.selectNodes.get(STARTING_NODE_ID)
    // @ts-expect-error@2339
    this.props.history.push(`/${node.id}/`)
    return node
  }

  _openExternal(url) {
    window.open(url, '_blank').focus()
  }
  // @ts-expect-error@2740
  transitioner: Transitioner = mobx.observable({
    getPathArray: () => {
      return this._pathArray
    },
    getValueCollection: () => {
      return this._getValueCollection()
    },
    getCurrentNode: (valued) => {
      return this._getCurrentNode(!!valued)
    },
    getAttributes: () => {
      return this._getAttributes()
    },
    goForward: mobx.action((id) => {
      return this._goForward(id, false)
    }),
    goBack: mobx.action(() => {
      return this._goBack()
    }),
    startOver: mobx.action(() => {
      return this._startOver()
    }),
    openExternal: mobx.action((url) => {
      return this._openExternal(url)
    }),
  })

  render() {
    return this.props.children
  }
}
// @ts-expect-error@2551
TransitionProvider.contextTypes = {
  models: PT.object.isRequired,
}

// @ts-expect-error@2339
TransitionProvider.childContextTypes = {
  transitioner: PT.object,
}

// @ts-expect-error@2339
TransitionProvider.propTypes = {
  children: PT.node,
  history: PT.object.isRequired,
}

const component = observer(TransitionProvider)
// @ts-expect-error@2339
component.displayName = 'TransitionProvider'
export default component
